import { create } from 'zustand';

import {
  AlertActionTarget,
  AlertAttribute,
  AlertBuilder,
} from '@/types/alerts';

import { SearchModel } from '../RunsTable/types';
import { validateThreshold } from './utils';

interface AlertStateStore extends AlertBuilder {
  setBanner: (
    banner:
      | { title: string; msg: string; status: 'success' | 'error' }
      | undefined
  ) => void;
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  setAttribute: (attribute: AlertBuilder['attribute']) => void;
  setAggregation: (aggregation: AlertBuilder['aggregation']) => void;
  setThreshold: (threshold: string) => void;
  setWindowMinutes: (windowMinutes: '5' | '15') => void;
  setOperator: (operator: AlertBuilder['operator']) => void;
  setFeedbackKey: (feedbackKey: string) => void;
  setActionTarget: (actionTarget: AlertBuilder['actionTarget']) => void;
  setPagerDutyConfig: (
    config: Partial<NonNullable<AlertBuilder['pagerDutyConfig']>>
  ) => void;
  setWebhookConfig: (
    config: Partial<NonNullable<AlertBuilder['webhookConfig']>>
  ) => void;
  resetState: (initialState: AlertBuilder) => void;
  setFilter: (value: SearchModel) => void;
}

export const INITIAL_RUN_COUNT_FILTER = `and(eq(status, "error"), eq(is_root, true))`;

export const INITIAL_LATENCY_FILTER = `eq(is_root, true)`;

export const INITIAL_STATE: AlertBuilder = {
  name: '',
  description: '',
  type: 'threshold',
  attribute: AlertAttribute.run_count,
  aggregation: 'sum',
  threshold: '',
  windowMinutes: '5',
  operator: 'gte',
  feedbackKey: '',
  actionTarget: AlertActionTarget.pagerduty,
  pagerDutyConfig: {},
  webhookConfig: { project_name: '' },
  isThresholdValid: true,
  filter: { filter: INITIAL_RUN_COUNT_FILTER },
  banner: undefined,
};

export const useAlertState = create<AlertStateStore>((set) => ({
  ...INITIAL_STATE,
  setBanner: (banner) =>
    set((state) => ({
      banner: banner
        ? {
            ...(state.banner || {}),
            ...banner,
          }
        : undefined,
    })),
  setName: (name) => set({ name }),
  setDescription: (description) => set({ description }),
  setAttribute: (attribute) => set({ attribute }),
  setAggregation: (aggregation) => set({ aggregation }),
  setThreshold: (threshold) => {
    set({
      threshold,
      isThresholdValid: validateThreshold(threshold),
    });
  },
  setWindowMinutes: (windowMinutes) => set({ windowMinutes }),
  setOperator: (operator) => set({ operator }),
  setFeedbackKey: (feedbackKey) => set({ feedbackKey }),
  setActionTarget: (actionTarget) => set({ actionTarget }),
  setPagerDutyConfig: (config) =>
    set((state) => ({
      pagerDutyConfig: {
        ...state.pagerDutyConfig,
        ...config,
      },
    })),
  setWebhookConfig: (config) =>
    set((state) => ({
      webhookConfig: {
        ...state.webhookConfig,
        ...config,
      },
    })),
  resetState: (initialState) => set(initialState),
  setFilter: (value) =>
    set((state) => ({
      filter: {
        ...state.filter,
        ...value,
      },
    })),
}));
